import { useState, useEffect, useCallback } from 'react';
import challengesApi from '../services/challengesApi';

export const useChallenges = () => {
  const [challenges, setChallenges] = useState([]);
  const [userChallenges, setUserChallenges] = useState([]);
  const [availableChallenges, setAvailableChallenges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchChallenges = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await challengesApi.getChallenges(options);
      const challengesList = Array.isArray(response) ? response : response.challenges || response.data || [];
      setChallenges(challengesList);
      return challengesList;
    } catch (error) {
      console.error('Error fetching challenges:', error);
      setError(error.message || 'Failed to load challenges');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserChallenges = useCallback(async () => {
    try {
      const response = await challengesApi.getUserChallenges();
      const userChallengesList = Array.isArray(response) ? response : response.challenges || response.data || [];
      setUserChallenges(userChallengesList);
      return userChallengesList;
    } catch (error) {
      console.error('Error fetching user challenges:', error);
      return [];
    }
  }, []);

  const fetchAvailableChallenges = useCallback(async () => {
    try {
      const response = await challengesApi.getAvailableChallenges();
      const availableList = Array.isArray(response) ? response : response.challenges || response.data || [];
      setAvailableChallenges(availableList);
      return availableList;
    } catch (error) {
      console.error('Error fetching available challenges:', error);
      return [];
    }
  }, []);

  const joinChallenge = useCallback(async (challengeId, teamId = null) => {
    try {
      const response = await challengesApi.joinChallenge(challengeId, teamId);
      
      // Refresh challenges to update joined status
      await Promise.all([
        fetchChallenges(),
        fetchUserChallenges()
      ]);
      
      return response;
    } catch (error) {
      console.error('Error joining challenge:', error);
      throw error;
    }
  }, [fetchChallenges, fetchUserChallenges]);

  const leaveChallenge = useCallback(async (challengeId) => {
    try {
      const response = await challengesApi.leaveChallenge(challengeId);
      
      // Refresh challenges to update joined status
      await Promise.all([
        fetchChallenges(),
        fetchUserChallenges()
      ]);
      
      return response;
    } catch (error) {
      console.error('Error leaving challenge:', error);
      throw error;
    }
  }, [fetchChallenges, fetchUserChallenges]);

  const createChallenge = useCallback(async (challengeData) => {
    try {
      const response = await challengesApi.createChallenge(challengeData);
      
      // Refresh challenges to include the new one
      await fetchChallenges();
      
      return response;
    } catch (error) {
      console.error('Error creating challenge:', error);
      throw error;
    }
  }, [fetchChallenges]);

  const refreshAllChallenges = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchChallenges(),
        fetchUserChallenges(),
        fetchAvailableChallenges()
      ]);
    } catch (error) {
      console.error('Error refreshing challenges:', error);
      setError('Failed to refresh challenges');
    } finally {
      setLoading(false);
    }
  }, [fetchChallenges, fetchUserChallenges, fetchAvailableChallenges]);

  // Load initial data
  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  return {
    challenges,
    userChallenges,
    availableChallenges,
    loading,
    error,
    fetchChallenges,
    fetchUserChallenges,
    fetchAvailableChallenges,
    joinChallenge,
    leaveChallenge,
    createChallenge,
    refreshAllChallenges,
    setError
  };
};